import {getLegacyLocaleFromLocale} from '@deezer/deezer-compatibility';
import {config} from './config';
import type {ReadonlyURLSearchParams} from 'next/navigation';
import {ATTRIBUTION_QUERY_PARAM} from '@deezer/logcenter';

const strip = (s: string) => s.split('#')[0].split('?')[0];

function safePathFrom(router: {
	locale?: string | null;
	asPath?: string;
	pathname?: string;
}) {
	let path = (router as any).asPath ?? (router as any).pathname ?? '/';
	path = strip(path);
	try {
		path = decodeURIComponent(path);
	} catch {
		/* empty */
	}

	const loc = router.locale || null;
	if (loc) {
		const re = new RegExp(`^/${loc}(?=/|$)`);
		path = path.replace(re, '') || '/';
	}

	if (path.includes('[')) path = '/';

	if (!path.startsWith('/')) path = '/' + path;

	return path;
}

export const getCurrentUrl = (router: {
	locale: string | null;
	pathname: string;
}) => {
	let currentUrl = `${config.get('appUrl')}${config.get('basePath')}`;
	if (router.locale) {
		currentUrl += `/${router.locale}`;
	}
	currentUrl += safePathFrom(router);
	if (!currentUrl.endsWith('/')) {
		currentUrl += '/';
	}
	return currentUrl;
};

const filterURLWithQuery = (params: URLSearchParams) => {
	ATTRIBUTION_QUERY_PARAM.forEach((param) => {
		params.delete(param);
	});
	return params;
};

type GetDeezerUrlOptions = {
	/** deezer-seo format locale (will be transformed to legacy "country-like" format).
	 * send undefined if we should not include the locale */
	locale?: string;
	/** pathname to target in www.deezer.com.
	 * If it already contains query they will be included (mandatory for footer links (ex payment gift link)*/
	pathname: string;
	/**
	 * additional query params to add
	 */
	params?: ReadonlyURLSearchParams | URLSearchParams | null;
};

export const getDeezerUrl = ({
	locale,
	pathname,
	params,
}: GetDeezerUrlOptions) => {
	const [path] = pathname.split('?');

	// Include the path to already include potential query string,
	// the path will be overriden anyway afterwards
	const url = new URL(`${config.get('deezerWebsiteUrl')}${pathname}`);
	if (params) {
		Array.from(params.entries()).forEach(([name, value]) => {
			url.searchParams.set(name, value);
		});
	}
	filterURLWithQuery(url.searchParams);
	url.pathname = locale ? `/${getLegacyLocaleFromLocale(locale)}${path}` : path;
	return url.href;
};
